import SimpleDialog from '@fuse/core/SimpleDialog/SimpleDialog';
import FuseLoading from '@fuse/core/FuseLoading';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon/FuseSvgIcon';
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteLangMutation, useGetAllTranslationsQuery } from 'src/app/services/spApi';
import { Close, Delete, Edit } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import TranslationsForm from './TranslationsForm';
import useMessage from 'src/app/hooks/useMessage';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import NoDataComponent from 'app/shared-components/noDataComponent/NoDataComponent';
import PaginationChangeRange from 'app/shared-components/PaginationChangeRange/PaginationChangeRange';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const Translations = () => {
  const { t, i18n } = useTranslation('translations');
  const [column, setColumn] = useState('Code');
  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [currentTranslation, setCurrentTranslation] = useState(null);
  const [open, setOpen] = useState(false);
  const { showMessage } = useMessage();
  const [isOpenedModal, setIsOpenedModal] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [name]);

  const {
    data: translations,
    isLoading,
    refetch: getTranslationsList,
  } = useGetAllTranslationsQuery(
    { page, rows: limit, column, name: name.trim() },
    { skip: name.trim().length > 1 && name.trim().length < 3 }
  );

  useEffect(() => {
    setColumn('Code');
  }, []);

  useEffect(() => {
    setColumn('Code');
  }, [i18n.language]);

  const [deleteTranslation] = useDeleteLangMutation();

  const totalPages = translations ? Math.ceil(translations.totalItems / limit) : 0;

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleEdit = (position) => {
    setCurrentTranslation(position);
    setOpen(true);
  };

  const handleDelete = (translation) => {
    deleteTranslation({ id: translation.id })
      .then((res) => {
        if (res) {
          getTranslationsList();
          showMessage({ message: t('TranslationSuccessfullyDeleted'), variant: 'success' });
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage({ message: t('ErrorOccuredOpenBrowserConsole'), variant: 'error' });
      });
  };

  const handleShowModal = (deleteMessage) => {
    setSelected(deleteMessage);
    setIsOpenedModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenedModal(false);
  };

  const showNewTranslationDialog = () => {
    setCurrentTranslation(null);
    setOpen(true);
  };

  return (
    <div className="flex flex-col flex-1">
      <TableContainer component={Paper} elevation={0}>
        <div className="flex flex-col flex-wrap items-center justify-between gap-10 mt-10 md:flex-row ">
          <h2 className="ml-5"> {t('Translations')}</h2>
          <div className="flex items-center gap-10">
            <div className="flex items-center">{t('Filter')}:</div>
            <div>
              <FormControl size="small">
                <InputLabel id="statuses-checkbox-label">{t('Column')}</InputLabel>
                <Select
                  value={column || t('Code')}
                  onChange={(e) => setColumn(e.target.value)}
                  input={<OutlinedInput label={t('Column')} />}
                  MenuProps={MenuProps}
                >
                  <MenuItem value={'Code'}>{t('Code')}</MenuItem>
                  <MenuItem value={'NameRu'}>{t('Name')} (рус)</MenuItem>
                  <MenuItem value={'NameUz'}>{t('Name')} (latin)</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div>
              <TextField
                size="small"
                label={t('Search')}
                value={name}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {name.length > 0 && (
                        <Close className="cursor-pointer text-14" onClick={() => setName('')} />
                      )}
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </div>
          </div>
          <Button
            className="mx-10"
            size="small"
            variant="outlined"
            color="secondary"
            onClick={showNewTranslationDialog}
          >
            <FuseSvgIcon size={20}>react-outline:plus</FuseSvgIcon>
            <span className="mx-8">{t('Add')}</span>
          </Button>
        </div>
        {isLoading && <FuseLoading />}
        {!isLoading && translations?.items?.length === 0 ? (
          <div className="flex items-center justify-center h-[70vh]">
            <NoDataComponent />
          </div>
        ) : (
          <Table className="w-full">
            <TableHead>
              <TableRow>
                <TableCell>{t('Code')}</TableCell>
                <TableCell>{t('Name')} (рус)</TableCell>
                <TableCell>{t('Name')} (latin)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {translations?.items?.map((item) => (
                <TableRow key={item.id} className="bg-white dark:bg-dark hover:bg-grey-200">
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.nameRu}</TableCell>
                  <TableCell>{item.nameUz}</TableCell>
                  <TableCell>
                    <div className="flex">
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        className="mr-5"
                        color="info"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        className="mr-5"
                        color="error"
                        onClick={() => handleShowModal(item)}
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <SimpleDialog
          title={currentTranslation ? t('Editing') : t('Adding')}
          open={open}
          onClose={() => setOpen(false)}
        >
          <TranslationsForm
            translation={currentTranslation}
            refetchList={getTranslationsList}
            onClose={() => setOpen(false)}
          />
        </SimpleDialog>
        {isOpenedModal && (
          <ConfirmDeleteModalWindow
            onClose={handleCloseModal}
            setIsOpenModal={setIsOpenedModal}
            modalTitle={'AreYouSureToDeleteTranslation'}
            deleteMessage={() => handleDelete(selected)}
            open={isOpenedModal}
          />
        )}
      </TableContainer>
      {totalPages > 1 && !isLoading && (
        <div className="flex items-center justify-between px-5">
          <PaginationChangeRange value={limit} setValue={setLimit} />
          <Pagination
            className="flex items-center justify-center mt-10"
            count={totalPages}
            page={page}
            onChange={handlePageChange}
          />
          <div className="w-[100px]" />
        </div>
      )}
    </div>
  );
};

export default Translations;
